import React, { useState, useEffect } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import RequiredLabel from '../CustomCommons/RequiredLabel';
import axios from 'axios';
import { BASE_URL } from '../Const/Url';
import { showToast } from '../../utils/ToastHelper';
import Loader from '../CustomCommons/Loader';
import { useHistory } from 'react-router-dom';
import * as path from '../Routes/RoutePaths';

const AddNewRole = () => {
  const history = useHistory();
  // Form state
  const [roleName, setRoleName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('active'); // 'active' or 'inactive'
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]); // Array of selected permission IDs
  const [categoriesWithActions, setCategoriesWithActions] = useState([]); // Categories from API
  const [isLoading, setIsLoading] = useState(true);
  const selectMode = 'multiple'; // Default to multiple select mode

  // Fetch permissions from API
  useEffect(() => {
    const fetchPermissions = () => {
      const url = `${BASE_URL}api/v1/auth/v2/category/permissions/`;
      axios
        .get(url)
        .then((res) => {
          if (res.data.status === 'success' && res.data.data) {
            // Transform API response to match our structure
            const transformedCategories = res.data.data.map((category) => ({
              id: category.id,
              key: `category_${category.id}`,
              name: category.name,
              actions: category.permissions.map((permission) => ({
                id: permission.id,
                name: permission.name,
                key: `permission_${permission.id}`
              }))
            }));
            setCategoriesWithActions(transformedCategories);
            setIsLoading(false);
          }
        })
        .catch((err) => {
          setIsLoading(false);
          const errorMsg = err.response?.data?.message || err.message || 'Failed to fetch permissions';
          showToast('error', errorMsg);
          console.error('Error fetching permissions:', err);
        });
    };

    fetchPermissions();
  }, []);

  // Handle permission checkbox change
  const handlePermissionChange = (permissionId, checked) => {
    setSelectedPermissionIds(prev => {
      if (selectMode === 'single') {
        // Single select mode: uncheck all other permissions in the same category
        const category = categoriesWithActions.find(cat => 
          cat.actions.some(action => action.id === permissionId)
        );
        if (!category) return prev;
        
        // Remove all permissions from this category
        const otherCategoryPermissionIds = category.actions.map(a => a.id);
        const filtered = prev.filter(id => !otherCategoryPermissionIds.includes(id));
        
        // Add the selected permission if checked
        return checked ? [...filtered, permissionId] : filtered;
      } else {
        // Multiple select mode: toggle the specific permission
        if (checked) {
          return [...prev, permissionId];
        } else {
          return prev.filter(id => id !== permissionId);
        }
      }
    });
  };

  // Handle "All Permission" checkbox
  const handleAllPermissionChange = (categoryId, checked) => {
    const category = categoriesWithActions.find(cat => cat.id === categoryId);
    if (!category) return;

    setSelectedPermissionIds(prev => {
      const categoryPermissionIds = category.actions.map(action => action.id);
      
      if (checked) {
        // Select all permissions in this category (add only those not already selected)
        const newIds = categoryPermissionIds.filter(id => !prev.includes(id));
        return [...prev, ...newIds];
      } else {
        // Deselect all permissions in this category
        return prev.filter(id => !categoryPermissionIds.includes(id));
      }
    });
  };

  // Check if all permissions are selected for a category
  const isAllPermissionSelected = (categoryId) => {
    const category = categoriesWithActions.find(cat => cat.id === categoryId);
    if (!category || category.actions.length === 0) return false;
    
    return category.actions.every(action => selectedPermissionIds.includes(action.id));
  };

  // Check if a specific permission is selected
  const isPermissionSelected = (permissionId) => {
    return selectedPermissionIds.includes(permissionId);
  };

  // Check if a category has any selected permissions
  const hasSelectedPermissions = (categoryId) => {
    const category = categoriesWithActions.find(cat => cat.id === categoryId);
    if (!category) return false;
    return category.actions.some(action => selectedPermissionIds.includes(action.id));
  };

  // Get count of selected permissions in a category
  const getSelectedCount = (categoryId) => {
    const category = categoriesWithActions.find(cat => cat.id === categoryId);
    if (!category) return 0;
    return category.actions.filter(action => selectedPermissionIds.includes(action.id)).length;
  };

  // Check if all permissions across all categories are selected
  const areAllPermissionsSelected = () => {
    if (categoriesWithActions.length === 0) return false;
    
    // Get all permission IDs from all categories
    const allPermissionIds = categoriesWithActions.flatMap(category => 
      category.actions.map(action => action.id)
    );
    
    // Check if all permissions are in selectedPermissionIds
    return allPermissionIds.length > 0 && 
           allPermissionIds.every(id => selectedPermissionIds.includes(id));
  };

  // Handle select/deselect all permissions across all categories
  const handleSelectAllPermissions = (checked) => {
    if (checked) {
      // Select all permissions from all categories
      const allPermissionIds = categoriesWithActions.flatMap(category => 
        category.actions.map(action => action.id)
      );
      setSelectedPermissionIds(allPermissionIds);
    } else {
      // Deselect all permissions
      setSelectedPermissionIds([]);
    }
  };

  // Get selected category data
  const selectedCategoryData = categoriesWithActions.find(
    cat => cat.id === selectedCategory
  );

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!roleName || roleName.trim() === '') {
      showToast('error', 'Role Name is required');
      return;
    }
    
    if (selectedPermissionIds.length === 0) {
      showToast('error', 'Please select at least one permission');
      return;
    }

    // Prepare data according to API format
    const data = {
      name: roleName.trim(),
      description: description.trim() || '',
      is_active: status === 'active' ? true : false, // Boolean: true for active, false for inactive
      permissions: selectedPermissionIds // Array of permission IDs
    };

    console.log('Submitting role data:', data);
    
    // API call to save role
    const url = `https://kaaruj-backend.sandbox.payinpos.com/api/v1/auth/v2/roles/`;
    axios.post(url, data)
      .then((res) => {
        if (res.data.status === 'success' || res.data.status) {

          // Reset form
          setRoleName('');
          setDescription('');
          setStatus('active');
          setSelectedPermissionIds([]);
          setSelectedCategory(null);
          showToast('success', 'Role Added Successfully');
          history.push(path.role_list);
        }
      })
      .catch((err) => {
        const errorMsg = err.response?.data?.message || 
                        err.response?.data?.errors?.msg || 
                        err.message || 
                        'Failed to add role';
        showToast('error', errorMsg);
        console.error('Error adding role:', err);
      });
  };

  // Show loader while fetching permissions
  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="categorey-parent pd-right" style={{ height: "100%", background: "#FAFAFA" }}>
      <div className="bg-white">
        {/* Header */}
        <div className="d-flex align-items-center" style={{ padding: "20px 35px", border: "0.5px solid #E0E0E0" }}>
          <h3 className="head-tag" style={{ marginTop: "6px" }}>Add Role</h3>
        </div>

        {/* Form Content */}
        <div style={{ padding: "20px 35px" }}>
          <form onSubmit={handleSubmit}>
            {/* Role Name and Description Row */}
            <div className="row mb-4">
              <div className="col-12 col-md-6 mb-3 mb-md-0">
                <RequiredLabel text="Role Name" />
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Type Role"
                    value={roleName}
                    onChange={(e) => setRoleName(e.target.value)}
                    required
                    style={{ backgroundColor: "#FAFAFA" }}
                  />
                </InputGroup>
              </div>
              <div className="col-12 col-md-6">
                <p style={{ marginBottom: "12px", fontSize: "14px", fontWeight: "500", color: "#1C1C1C" }}>
                  Status
                </p>
                <div className="d-flex gap-4">
                  <div className="d-flex align-items-center">
                    <input
                      type="radio"
                      name="status"
                      id="status-active"
                      value="active"
                      checked={status === 'active'}
                      onChange={(e) => setStatus(e.target.value)}
                      className="form-check-input"
                      style={{ marginRight: "8px", cursor: "pointer" }}
                    />
                    <label htmlFor="status-active" style={{ margin: 0, cursor: "pointer" }}>
                      Active
                    </label>
                  </div>
                  <div className="d-flex align-items-center">
                    <input
                      type="radio"
                      name="status"
                      id="status-inactive"
                      value="inactive"
                      checked={status === 'inactive'}
                      onChange={(e) => setStatus(e.target.value)}
                      className="form-check-input"
                      style={{ marginRight: "8px", cursor: "pointer" }}
                    />
                    <label htmlFor="status-inactive" style={{ margin: 0, cursor: "pointer" }}>
                      Inactive
                    </label>
                  </div>
                </div>
                <p style={{ marginTop: "8px", fontSize: "12px", color: "#828282" }}>
                  Active roles can be assigned to users
                </p>
              </div>
              <div className="col-12">
                <p style={{ marginBottom: "8px", fontSize: "14px", color: "#828282" }}>
                  Description (optional)
                </p>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Type Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  style={{ backgroundColor: "#FAFAFA", resize: "none" }}
                />
              </div>
            </div>

            {/* Checkbox for select all permission */}
            <div className="row mb-3" style={{ marginTop: "20px" }}>
              <div className="col-12">
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 16px",
                  backgroundColor: "#F5F5F5",
                  borderRadius: "4px",
                  border: "1px solid #E0E0E0"
                }}>
                  <input
                    type="checkbox"
                    id="select-all-permissions"
                    checked={areAllPermissionsSelected()}
                    onChange={(e) => handleSelectAllPermissions(e.target.checked)}
                    style={{
                      width: "18px",
                      height: "18px",
                      cursor: "pointer",
                      marginRight: "12px"
                    }}
                  />
                  <label 
                    htmlFor="select-all-permissions"
                    style={{ 
                      margin: 0, 
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#1C1C1C",
                      flex: 1
                    }}
                  >
                    Select All Permissions
                  </label>
                </div>
              </div>
            </div>

            {/* Category and Permission Panels */}
            <div className="row" style={{ marginTop: "30px" }}>
              {/* Category Panel (Left) */}
              <div className="col-12 col-md-3" style={{ paddingRight: "15px" }}>
                <div style={{ 
                  backgroundColor: "#212121", 
                  color: "white", 
                  padding: "12px 16px",
                  borderRadius: "4px 4px 0 0",
                  fontSize: "14px",
                  fontWeight: "500"
                }}>
                  Category
                </div>
                <div style={{
                  backgroundColor: "white",
                  border: "1px solid #E0E0E0",
                  borderTop: "none",
                  borderRadius: "0 0 4px 4px",
                  minHeight: "400px",
                  maxHeight: "500px",
                  overflowY: "auto"
                }}>
                  {categoriesWithActions.map((category) => {
                    const isSelected = selectedCategory === category.id;
                    const hasSelections = hasSelectedPermissions(category.id);
                    const selectedCount = getSelectedCount(category.id);
                    const totalCount = category.actions.length;
                    
                    // Determine background color based on selection state
                    // Priority: Selected state > Has selections > Default
                    let backgroundColor = "white";
                    if (isSelected) {
                      // Always show light blue when category is selected, regardless of permission selections
                      backgroundColor = "#E3F2FD";
                    } else if (hasSelections) {
                      // Show light green only when category has selections but is not currently selected
                      backgroundColor = "#E8F5E9";
                    }
                    
                    return (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => setSelectedCategory(category.id)}
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          border: "none",
                          borderBottom: "1px solid #E0E0E0",
                          // backgroundColor: backgroundColor,
                          color: isSelected ? "#2F80ED" : "#1C1C1C",
                          textAlign: "left",
                          cursor: "pointer",
                          fontSize: "14px",
                          fontWeight: isSelected ? "500" : "400",
                          transition: "all 0.2s",
                          position: "relative"
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.target.style.backgroundColor = hasSelections ? "#C8E6C9" : "#F5F5F5";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = backgroundColor;
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                          <span>{category.name}</span>
                          {hasSelections && (
                            <span style={{
                              backgroundColor: "#4CAF50",
                              color: "white",
                              borderRadius: "12px",
                              padding: "2px 8px",
                              fontSize: "12px",
                              fontWeight: "500",
                              minWidth: "24px",
                              textAlign: "center",
                              border: "none",
                              outline: "none"
                            }}>
                              {selectedCount}/{totalCount}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Action and Permission Panel (Right) */}
              <div className="col-12 col-md-9" style={{ paddingLeft: "15px" }}>
                {selectedCategoryData ? (
                  <>
                    <div style={{ 
                      backgroundColor: "#212121", 
                      color: "white", 
                      padding: "12px 16px",
                      borderRadius: "4px 4px 0 0",
                      fontSize: "14px",
                      fontWeight: "500",
                      display: "flex",
                      justifyContent: "space-between"
                    }}>
                      <span>Action</span>
                      <span>Permission</span>
                    </div>
                    <div style={{
                      backgroundColor: "white",
                      border: "1px solid #E0E0E0",
                      borderTop: "none",
                      borderRadius: "0 0 4px 4px",
                      minHeight: "400px",
                      maxHeight: "500px",
                      overflowY: "auto"
                    }}>
                      {/* All Permission Checkbox */}
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        borderBottom: "1px solid #E0E0E0"
                      }}>
                        <label style={{ 
                          margin: 0, 
                          cursor: "pointer",
                          fontSize: "14px",
                          color: "#1C1C1C",
                          flex: 1
                        }}>
                          All Permission
                        </label>
                        <input
                          type="checkbox"
                          checked={isAllPermissionSelected(selectedCategory)}
                          onChange={(e) => handleAllPermissionChange(selectedCategory, e.target.checked)}
                          style={{
                            width: "18px",
                            height: "18px",
                            cursor: "pointer"
                          }}
                        />
                      </div>

                      {/* Individual Action Checkboxes */}
                      {selectedCategoryData.actions.map((action) => {
                        const isChecked = isPermissionSelected(action.id);
                        return (
                          <div
                            key={action.id}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "12px 16px",
                              borderBottom: "1px solid #E0E0E0"
                            }}
                          >
                            <label 
                              onClick={() => handlePermissionChange(action.id, !isChecked)}
                              style={{ 
                                margin: 0, 
                                cursor: "pointer",
                                fontSize: "14px",
                                color: "#1C1C1C",
                                flex: 1
                              }}
                            >
                              {action.name}
                            </label>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => handlePermissionChange(action.id, e.target.checked)}
                              style={{
                                width: "18px",
                                height: "18px",
                                cursor: "pointer"
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div style={{
                    backgroundColor: "white",
                    border: "1px solid #E0E0E0",
                    borderRadius: "4px",
                    minHeight: "400px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#828282",
                    fontSize: "14px"
                  }}>
                    Please select a category to view actions and permissions
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-4 d-flex justify-content-end">
              <button
                type="submit"
                className="attribute-submit"
              >
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddNewRole;
